import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import { ValidationError } from "./types";
import { pitchesApi } from "../../../repositories/pitches";
import * as Yup from "yup";

interface CSVUploadProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface ReleaseData {
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  releaseUPC?: string;
  trackISRC?: string;
  status?:string
}

const CSVUpload: React.FC<CSVUploadProps> = ({ onClose, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [csvData, setCsvData] = useState<ReleaseData[]>([]);

  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [parsing, setParsing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<ReleaseData | null>(null);


  const validateReleaseDate = (
    dateString: string
  ): { isValid: boolean; error?: string } => {
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    if (!dateRegex.test(dateString)) {
      return { isValid: false, error: "Date must be in MM/DD/YYYY format" };
    }
    const [month, day, year] = dateString.split("/").map(Number);
    const releaseDate = new Date(year, month - 1, day);
    const today = new Date();
    if (isNaN(releaseDate.getTime())) {
      return { isValid: false, error: "Invalid date" };
    }
    if (releaseDate <= today) {
      return { isValid: false, error: "Release date must be in the future" };
    }
    return { isValid: true };
  };

  const downloadTemplate = () => {
    const templateData = [
      [
        "Artist Name",
        "Release Title",
        "Release Date",
        "Release UPC",
        "Track ISRC",
      ],
      [
        "Example Artist",
        "Example Release",
        "12/25/2025",
        "123332445454",
        "USRC17607839",
      ],
      [
        "Another Artist",
        "Another Release",
        "01/15/2026",
        "123432224456",
        "USRC17607840",
      ],
      ["Third Artist", "Third Release", "02/10/2026", "", ""],
    ];
    const csvContent = templateData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Release_Schedule_Template.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const optionalIdentifiersSchema = Yup.object().shape({
    releaseUPC: Yup.string()
      .matches(/^\d{12}$/, "Release UPC must be exactly 12 digits")
      .nullable()
      .notRequired(),
    trackISRC: Yup.string()
      .matches(
        /^[A-Z]{2}-?[A-Z0-9]{3}-?\d{2}-?\d{5}$/,
        "Track ISRC format is invalid"
      )
      .nullable()
      .notRequired(),
  });

  const parseAndValidateCSV = (csvText: string) => {
    const lines = csvText.split("\n");
    const headers = lines[0]
      .split(",")
      .map((h) => h.trim().replace(/^\ufeff/, ""));
    const allRows: ReleaseData[] = [];
    const errors: ValidationError[] = [];
    const requiredHeaders = ["Artist Name", "Release Title", "Release Date"];
    const missingHeaders = requiredHeaders.filter(
      (header) => !headers.some((h) => h.toLowerCase() === header.toLowerCase())
    );
    if (missingHeaders.length > 0) {
      errors.push({
        row: 1,
        field: "Headers",
        message: `Missing required columns: ${missingHeaders.join(", ")}`,
        severity: "error",
      });
      return { allRows, errors };
    }
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      const values = line.split(",");
      const row: any = {};
      let hasErrors = false;
      headers.forEach((header, index) => {
        row[header] = values[index]?.trim() || "";
      });

      // Required fields check
      const missingFields = requiredHeaders.filter((field) => {
        const headerKey = headers.find(
          (h) => h.toLowerCase() === field.toLowerCase()
        );
        return !headerKey || !row[headerKey] || row[headerKey] === "";
      });
      if (missingFields.length > 0) {
        errors.push({
          row: i, // instead of i + 1
          field: "Required Fields",
          message: `Missing required fields: ${missingFields.join(", ")}`,
          severity: "error",
        });
        hasErrors = true;
      }

      // Release Date validation
      const releaseDateHeader = headers.find(
        (h) => h.toLowerCase() === "release date"
      );
      if (releaseDateHeader && row[releaseDateHeader]) {
        const dateValidation = validateReleaseDate(row[releaseDateHeader]);
        if (!dateValidation.isValid) {
          errors.push({
            row: i, // instead of i + 1
            field: "Release Date",
            message: dateValidation.error || "Invalid date format",
            severity: "error",
          });
          hasErrors = true;
        } else {
          // Check if release date is not more than 6 months in future
          const [month, day, year] = row[releaseDateHeader]
            .split("/")
            .map(Number);
          const releaseDate = new Date(year, month - 1, day);
          const today = new Date();
          // Add 6 months to today
          const sixMonthsLater = new Date(today);
          sixMonthsLater.setMonth(today.getMonth() + 6);

          if (releaseDate > sixMonthsLater) {
            errors.push({
              row: i, // instead of i + 1
              field: "Release Date",
              message:
                "Release date cannot be more than 6 months in the future",
              severity: "error",
            });
            hasErrors = true;
          }
        }
      }

      // UPC/ISRC validation (optional, but must be valid if present)
      const releaseUPCHeader = headers.find(
        (h) => h.toLowerCase() === "release upc"
      );
      const trackISRCHeader = headers.find(
        (h) => h.toLowerCase() === "track isrc"
      );
      try {
        optionalIdentifiersSchema.validateSync(
          {
            releaseUPC: releaseUPCHeader
              ? row[releaseUPCHeader] || undefined
              : undefined,
            trackISRC: trackISRCHeader
              ? row[trackISRCHeader] || undefined
              : undefined,
          },
          { abortEarly: false }
        );
      } catch (err: any) {
        if (err.inner && Array.isArray(err.inner)) {
          err.inner.forEach((e: any) => {
            errors.push({
              row: i, // instead of i + 1
              field: e.path === "releaseUPC" ? "Release UPC" : "Track ISRC",
              message: e.message,
              severity: "error",
            });
          });
        } else if (err.message) {
          errors.push({
            row: i, // instead of i + 1
            field: "UPC/ISRC",
            message: err.message,
            severity: "error",
          });
        }
        hasErrors = true;
      }

      if (true) {
        const artistNameHeader = headers.find(
          (h) => h.toLowerCase() === "artist name"
        );
        const releaseTitleHeader = headers.find(
          (h) => h.toLowerCase() === "release title"
        );
        const releaseDateHeader = headers.find(
          (h) => h.toLowerCase() === "release date"
        );
        const releaseUPCHeader = headers.find(
          (h) => h.toLowerCase() === "release upc"
        );
        const trackISRCHeader = headers.find(
          (h) => h.toLowerCase() === "track isrc"
        );
        const releaseData: ReleaseData = {
          artistName: row[artistNameHeader!]?.trim() || "",
          releaseTitle: row[releaseTitleHeader!]?.trim() || "",
          releaseDate: row[releaseDateHeader!]?.trim() || "",
          releaseUPC: releaseUPCHeader ? row[releaseUPCHeader]?.trim() || "" : "",
          trackISRC: trackISRCHeader ? row[trackISRCHeader]?.trim() || "" : "",
        };
        allRows.push(releaseData);
      }
    }
    return { allRows, errors };
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    console.log("Dropped file: ==>", file);
    if (file) {
      setParsing(true);
      setShowPreview(false)
      setSelectedFile(file);
      try {
        const csvText = await file.text();
        const { allRows, errors } = parseAndValidateCSV(csvText);
        const checkDuplicateArtist = await pitchesApi.checkDuplicateArtist(allRows.map(r => r.artistName));
       
        const duplicates = checkDuplicateArtist.data.data.duplicates || [];
      
        const duplicateNames = new Set(duplicates.map((d: any) => d.artistName));
  
        const rowsWithStatus = allRows.map(row => ({
          ...row,
          status: duplicateNames.has(row.artistName) ? "Roster Match" : "New artist"
        }));
        setCsvData(rowsWithStatus);
        setValidationErrors(errors);
        setShowPreview(true);
      } catch (err: any) {
        Swal.fire({
          title: "CSV Parsing Error",
          text: "Failed to parse CSV file. Please check the format.",
          icon: "error",
        });
      } finally {
        setParsing(false);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".csv"],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  const handleImport = async () => {
    try {
      setUploading(true);

      // Validate all entries before import (after possible edits)
      const validationErrors: string[] = [];
      csvData.forEach((row, idx) => {
        // Validate required fields
    
        if (!row.artistName || !row.releaseTitle || !row.releaseDate) {
          validationErrors.push(`Row ${idx + 1}: Missing required fields.`);
        }

        // Validate release date format and range
        const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
        if (!dateRegex.test(row.releaseDate)) {
          validationErrors.push(
            `Row ${idx + 1}: Release date must be in MM/DD/YYYY format.`
          );
        } else {
          const [month, day, year] = row.releaseDate.split("/").map(Number);
          const releaseDate = new Date(year, month - 1, day);
          const today = new Date();
          const sixMonthsLater = new Date(today);
          sixMonthsLater.setMonth(today.getMonth() + 6);
          if (releaseDate <= today) {
            validationErrors.push(
              `Row ${idx + 1}: Release date must be in the future.`
            );
          }
          if (releaseDate > sixMonthsLater) {
            validationErrors.push(
              `Row ${idx + 1
              }: Release date cannot be more than 6 months in the future.`
            );
          }
        }

        // Validate UPC/ISRC if present
        try {
          optionalIdentifiersSchema.validateSync(
            {
              releaseUPC: row.releaseUPC || undefined,
              trackISRC: row.trackISRC || undefined,
            },
            { abortEarly: false }
          );
        } catch (err: any) {
          if (err.inner && Array.isArray(err.inner)) {
            err.inner.forEach((e: any) => {
              validationErrors.push(`Row ${idx + 1}: ${e.message}`);
            });
          } else if (err.message) {
            validationErrors.push(`Row ${idx + 1}: ${err.message}`);
          }
        }
      });

      if (validationErrors.length > 0) {
        Swal.fire({
          title: "Validation Error",
          html: `
            <div class="text-left">
              <h3 class="text-lg font-medium text-red-700 mb-2">Please fix the following errors:</h3>
              <ul class="text-sm text-gray-700" style="padding-left:16px;">
                ${validationErrors.map((e) => `<li>${e}</li>`).join("")}
              </ul>
              <p class="text-xs text-gray-500 mt-2">Correct these fields before importing.</p>
            </div>
          `,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#EF4444",
        });
        setUploading(false);
        return;
      }

      if (!csvData || csvData.length === 0) {
        Swal.fire({
          title: "No Data to Import",
          text: "No valid release schedule data available for import.",
          icon: "warning",
          confirmButtonText: "OK",
        });
        return;
      }
      Swal.fire({
        title: "Importing Release Schedule...",
        html: `
          <div class="text-center">
            <div class="w-12 h-12 border-b-2 border-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p class="text-sm text-gray-600" id="import-progress">Processing releases...</p>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await pitchesApi.importCSV(csvData);
      // if (!response || response.status >= 400 || !response.data || !response.data.success) {
      //   if (response?.status === 400 && response?.data?.conflicts) {
      //     Swal.close();
      //     Swal.fire({
      //       title: 'Duplicate Entries Found',
      //       html: `
      //         <div class="text-center">
      //           <div class="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
      //             <svg class="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      //               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01"></path>
      //             </svg>
      //           </div>
      //           <h3 class="text-lg font-medium text-gray-900 mb-2">The following entries already exist:</h3>
      //           <ul class="text-sm text-gray-700 mb-2" style="text-align:left;">
      //             ${response.data.conflicts.map((entry: any) =>
      //               `<li><b>${entry.artistName}</b> - <b>${entry.releaseTitle}</b></li>`
      //             ).join('')}
      //           </ul>
      //           <p class="text-xs text-gray-500">Please remove or update these entries before importing.</p>
      //           <div style="max-height:120px;overflow-y:auto;border:1px solid #eee;border-radius:6px;background:#fafafa;margin-top:8px;">
      //             <ul class="text-sm text-gray-700" style="text-align:left;list-style:none;padding:8px;">
      //               ${response.data.conflicts.map((entry: any) =>
      //                 `<li style="padding:4px 0;border-bottom:1px solid #f0f0f0;">
      //                   <b>${entry.artistName}</b> - <b>${entry.releaseTitle}</b>
      //                 </li>`
      //               ).join('')}
      //             </ul>
      //           </div>
      //         </div>
      //       `,
      //       icon: 'warning',
      //       confirmButtonText: 'OK',
      //       confirmButtonColor: '#F59E42'
      //     });
      //     return;
      //   }
      //   throw new Error(response?.data?.message || 'Release schedule import failed');
      // }
      Swal.close();
      Swal.fire({
        title: "Import Successful!",
        html: `
          <div class="text-center">
            <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">Release Schedule Import Complete</h3>
            <p class="text-sm text-gray-600">
              Successfully imported <strong>${response.data.totalSuccess || csvData.length
          }</strong> releases
            </p>
            <p class="text-xs text-gray-500 mt-2">
              These releases are now available with <span class="text-red-600 font-medium">"Needs Pitch"</span> status in your dashboard
            </p>
            <p class="text-xs text-gray-400 mt-1">
              Click on any "Needs Pitch" status to create a pitch for that release
            </p>
          </div>
        `,
        icon: "success",
        confirmButtonText: "View Dashboard",
        confirmButtonColor: "#10B981",
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      Swal.close();

      // Show duplicate modal if message contains "Duplicate entries"
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data?.existingEntries
      ) {
        Swal.fire({
          title: "Duplicate Entries Found",
          html: `
            <div class="text-center">
              <div class="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01"></path>
                </svg>
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Entries already exist in DB</h3>
              <p class="text-sm text-gray-700 mb-2">${error.response.data.message
            }</p>
              <p class="text-xs text-gray-500">Please remove or update these entries before importing.</p>
              <div style="max-height:132px;overflow-y:auto;border-radius:8px;background:#f9fafb;margin-top:12px;box-shadow:0 1px 4px rgba(0,0,0,0.04);border:1px solid #e5e7eb;padding:0 4px;">
                ${error.response.data.existingEntries
              .map(
                (entry: any) =>
                  `<div style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px 16px;
                    margin: 4px 0;
                    background: #fff;
                    border-radius: 6px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
                    border: 1px solid #f3f4f6;
                    font-size: 15px;
                  ">
                    <span style="font-weight:600;color:#374151;">
                      ${entry.artistName}
                    </span>
                    <span style="font-weight:500;color:#2563eb;">
                      ${entry.releaseTitle}
                    </span>
                  </div>`
              )
              .join("")}
              </div>
            </div>
          `,
          icon: "warning",
          confirmButtonText: "OK",
          confirmButtonColor: "#F59E42",
        });
        return;
      }

      Swal.fire({
        title: "Import Failed",
        html: `
          <div class="text-center">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">Release Schedule Import Failed</h3>
            <p class="text-sm text-gray-600 mb-2">
              ${error.response.message
          } || 'Failed to import release schedule. Please try again.'}
            </p>
            <p class="text-xs text-gray-400">
              Please check your CSV format and try again.
            </p>
            ${error.response?.status
            ? `<p class="text-xs text-gray-300 mt-2">Error Code: ${error.response.status}</p>`
            : ""
          }
          </div>
        `,
        icon: "error",
        confirmButtonText: "Try Again",
        confirmButtonColor: "#EF4444",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleEditSave = () => {
    if (editingIndex !== null && editForm) {
      const updated = [...csvData];
      updated[editingIndex] = editForm;
      setCsvData(updated);

      // Remove previous errors for this row
      const errors = validationErrors.filter(e => e.row !== editingIndex + 1);

      // Re-validate the edited entry
      // Required fields
      if (!editForm.artistName || !editForm.releaseTitle || !editForm.releaseDate) {
        errors.push({
          row: editingIndex + 1,
          field: 'Required Fields',
          message: `Missing required fields.`,
          severity: 'error'
        });
      }

      // Release Date
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
      if (!dateRegex.test(editForm.releaseDate)) {
        errors.push({
          row: editingIndex + 1,
          field: 'Release Date',
          message: 'Release date must be in MM/DD/YYYY format.',
          severity: 'error'
        });
      } else {
        const [month, day, year] = editForm.releaseDate.split('/').map(Number);
        const releaseDate = new Date(year, month - 1, day);
        const today = new Date();
        const sixMonthsLater = new Date(today);
        sixMonthsLater.setMonth(today.getMonth() + 6);
        if (releaseDate <= today) {
          errors.push({
            row: editingIndex + 1,
            field: 'Release Date',
            message: 'Release date must be in the future.',
            severity: 'error'
          });
        }
        if (releaseDate > sixMonthsLater) {
          errors.push({
            row: editingIndex + 1,
            field: 'Release Date',
            message: 'Release date cannot be more than 6 months in the future.',
            severity: 'error'
          });
        }
      }

      // UPC/ISRC
      try {
        optionalIdentifiersSchema.validateSync(
          {
            releaseUPC: editForm.releaseUPC || undefined,
            trackISRC: editForm.trackISRC || undefined,
          },
          { abortEarly: false }
        );
      } catch (err: any) {
        if (err.inner && Array.isArray(err.inner)) {
          err.inner.forEach((e: any) => {
            errors.push({
              row: editingIndex + 1,
              field: e.path === "releaseUPC" ? "Release UPC" : "Track ISRC",
              message: e.message,
              severity: 'error'
            });
          });
        } else if (err.message) {
          errors.push({
            row: editingIndex + 1,
            field: 'UPC/ISRC',
            message: err.message,
            severity: 'error'
          });
        }
      }

      setValidationErrors(errors);
      setEditingIndex(null);
      setEditForm(null);
    }
  };

  const handleRejectEntry = (index: number) => {
    setCsvData(csvData.filter((_, i) => i !== index));
    setValidationErrors(validationErrors.filter(e => e.row !== index + 1));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div
        className="relative w-11/12 max-w-6xl p-5 mx-auto overflow-y-auto bg-white border rounded-md shadow-lg top-10"
        style={{ maxHeight: "80vh" }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {uploading
              ? "Importing Release Schedule..."
              : parsing
                ? "Parsing CSV..."
                : showPreview
                  ? "Release Schedule Preview"
                  : "Upload Release Schedule"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={uploading || parsing}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        {parsing && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 border-b-2 border-indigo-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-sm text-gray-600">Parsing CSV file...</p>
          </div>
        )}
        {!showPreview && !parsing && (
          <div className="space-y-4">
            <div className="flex justify-center mb-6">
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg bg-blue-50 hover:bg-blue-100"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download Template (Release_Schedule_Template.csv)
              </button>
            </div>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${isDragActive
                  ? "border-indigo-500 bg-indigo-50"
                  : "border-gray-300 hover:border-gray-400"
                }`}
            >
              <input {...getInputProps()} />
              <svg
                className="w-12 h-12 mx-auto text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <div className="mt-4">
                <p className="text-lg font-medium text-gray-900">
                  {isDragActive
                    ? "Drop your CSV file here"
                    : "Drag & drop your release schedule CSV here"}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  or click to browse files (max 10MB)
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Required: Artist Name, Release Title, Release Date
                  (MM/DD/YYYY)
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Optional: Release UPC, Track ISRC
                </p>
              </div>
            </div>
          </div>
        )}
        {showPreview && !parsing && (
          <div className="space-y-4">
            {validationErrors.length > 0 && (
              <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                <h4 className="font-medium text-red-800">Validation Errors:</h4>
                <div className="mt-2 space-y-1">
                  {validationErrors.map((error, index) => (
                    <p key={index} className="text-sm text-red-700">
                      Row {error.row}: {error.message}
                    </p>
                  ))}
                </div>
              </div>
            )}
            {csvData.length > 0 && (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full bg-white">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-sm font-medium text-left text-gray-700">
                        Artist Name
                      </th>
                      <th className="px-4 py-2 text-sm font-medium text-left text-gray-700">
                        Release Title
                      </th>
                      <th className="px-4 py-2 text-sm font-medium text-left text-gray-700">
                        Release Date
                      </th>
                      <th className="px-4 py-2 text-sm font-medium text-left text-gray-700">
                        Release UPC
                      </th>
                      <th className="px-4 py-2 text-sm font-medium text-left text-gray-700">
                        Track ISRC
                      </th>  
                      <th className="px-4 py-2 text-sm font-medium text-left text-gray-700">
                        status
                      </th>   
                      <th className="px-4 py-2 text-sm font-medium text-left text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {csvData.map((release, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {release.artistName}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {release.releaseTitle}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {release.releaseDate}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">
                          {release.releaseUPC || "N/A"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">
                          {release.trackISRC || "N/A"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">
                        {release.status}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <button
                            className="text-blue-600 hover:underline"
                            onClick={() => {
                              setEditingIndex(index);
                              setEditForm({ ...csvData[index] });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="ml-2 text-red-600 hover:underline"
                            onClick={() => handleRejectEntry(index)}
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-between pt-4">
              <button
                onClick={() => {
                  setShowPreview(false);
                  setSelectedFile(null);
                  setCsvData([]);
                  setValidationErrors([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
                disabled={uploading}
              >
                Back to Upload
              </button>
              <button
                onClick={handleImport}
                disabled={
                  uploading ||
                  csvData.length === 0 ||
                  validationErrors.some((e) => e.severity === "error")
                }
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading
                  ? "Importing..."
                  : `Import ${csvData.length} Release${csvData.length !== 1 ? "s" : ""
                  }`}
              </button>
            </div>
          </div>
        )}
        {editingIndex !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="w-full max-w-md p-6 bg-white rounded shadow-lg">
              <h4 className="mb-4 font-bold">Edit Entry</h4>
              <input
                className="w-full px-2 py-1 mb-2 border"
                value={editForm?.artistName || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm!, artistName: e.target.value })
                }
                placeholder="Artist Name"
              />
              <input
                className="w-full px-2 py-1 mb-2 border"
                value={editForm?.releaseTitle || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm!, releaseTitle: e.target.value })
                }
                placeholder="Release Title"
              />
              <input
                className="w-full px-2 py-1 mb-2 border"
                value={editForm?.releaseDate || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm!, releaseDate: e.target.value })
                }
                placeholder="Release Date"
              />
              <input
                className="w-full px-2 py-1 mb-2 border"
                value={editForm?.releaseUPC || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm!, releaseUPC: e.target.value })
                }
                placeholder="Release UPC"
              />
              <input
                className="w-full px-2 py-1 mb-2 border"
                value={editForm?.trackISRC || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm!, trackISRC: e.target.value })
                }
                placeholder="Track ISRC"
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="px-3 py-1 bg-gray-200 rounded"
                  onClick={() => setEditingIndex(null)}
                >
                  Cancel
                </button>
                <button
                  className="px-3 py-1 text-white bg-green-600 rounded"
                  onClick={handleEditSave}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVUpload;
